// Utils for ErasureSetLayout
import { CAInfoServer, CAServerDrive, ClusterAnalysis } from "../types";
import {
  ErasureSortProcess,
  EsetDrives,
  ExtendedDriveData,
} from "./ErasureSetLayout.types";

export const processDiskList = (
  analysis: ClusterAnalysis | null,
  sortBy: "sets" | "server" | "used-space" | "capacity" | "status",
): ErasureSortProcess => {
  if (!analysis) {
    return { drives: [] };
  }
  const dataUse = analysis.minio.info;
  const servers = dataUse.servers;
  let drvsRet: ExtendedDriveData[] = [];
  let esets: EsetDrives = {};
  // We iterate over all servers to get the drives, if sort by 'sets' is selected we store them and sort according this parameter
  if (servers) {
    servers.forEach((srvData: CAInfoServer, indxServ) => {
      const drives = srvData.drives;
      // We review the endpoint
      const serverEndpoint = srvData.endpoint;

      // Default pool name is going to be serverEndpoint
      let pool = -1;
      if (srvData.drives) {
        pool = srvData.drives[0].pool_index;
      }

      if (!drives) {
        return;
      }

      drives.forEach((driveInfo) => {
        setOptionalServerFields(srvData, driveInfo);
        const extDriveData = {
          driveInfo,
          pool,
          server: indxServ,
          eset: driveInfo.set_index,
          serverEndpoint: serverEndpoint,
          driveEndpoint: driveInfo.endpoint,
        };

        if (sortBy === "sets") {
          //Pool doesn't exist, we create the pool and add it's first element to the list
          if (!esets[`${driveInfo.set_index}`]) {
            esets[`${driveInfo.set_index}`] = [extDriveData];
          } else {
            //Pool already exists, we just add the new item there
            esets[`${driveInfo.set_index}`] = [
              ...esets[`${driveInfo.set_index}`],
              extDriveData,
            ];
          }
        } else {
          drvsRet.push(extDriveData);
        }
      });
    });
  }

  if (sortBy === "sets") {
    const keys = Object.keys(esets);
    keys.sort((a, b) => parseInt(a) - parseInt(b));
    keys.forEach((esetKey) => {
      drvsRet = [...drvsRet, ...esets[`${esetKey}`]];
    });
  } else if (sortBy === "status") {
    const isDriveOffline = (driveInfo: CAServerDrive) =>
      driveInfo.state !== "ok" && driveInfo.state !== "warning";

    drvsRet = [
      ...drvsRet.filter((driveExtended) =>
        isDriveOffline(driveExtended.driveInfo),
      ),
      ...drvsRet.filter(
        (driveExtended) => !isDriveOffline(driveExtended.driveInfo),
      ),
    ];
  } else if (sortBy === "used-space") {
    drvsRet.sort((a, b) => a.driveInfo.usedspace - b.driveInfo.usedspace);
  } else if (sortBy === "capacity") {
    drvsRet.sort((a, b) => a.driveInfo.totalspace - b.driveInfo.totalspace);
  }

  return { drives: drvsRet };
};

export const setOptionalServerFields = (
  srvData: CAInfoServer,
  driveInfo: CAServerDrive,
) => {
  // flag offline drives
  if (driveInfo.state !== "ok" && driveInfo.state !== "warning") {
    srvData.offlineDrivesCount = (srvData.offlineDrivesCount || 0) + 1;
  }
  if (driveInfo.healing) {
    srvData.healingDrivesCount = (srvData.healingDrivesCount || 0) + 1;
  }
  if (
    driveInfo.metrics &&
    (driveInfo.metrics.totalErrorsTimeout > 0 ||
      driveInfo.metrics.totalErrorsAvailability > 0)
  ) {
    srvData.drivesWithErrorsCount = (srvData.drivesWithErrorsCount || 0) + 1;
  }
  let usedPerc = (driveInfo.usedspace / driveInfo.totalspace) * 100;
  let usedInodes =
    (driveInfo.used_inodes / (driveInfo.used_inodes + driveInfo.free_inodes)) *
    100;
  if (usedPerc >= 90) {
    srvData.drivesRunningOutOfSpaceCount =
      (srvData.drivesRunningOutOfSpaceCount || 0) + 1;
  }
  if (usedInodes >= 90) {
    srvData.drivesRunningOutOfInodesCount =
      (srvData.drivesRunningOutOfInodesCount || 0) + 1;
  }
};
